import { Flex, Stack, useMantineTheme } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@feathr/components';
import Page from '@feathr/extender/App/Page';
import { useAccount, useUser } from '@feathr/extender/state';

import { useMetaIntegration, useMetaSync } from './MetaIntegrationPage.hooks';
import {
  getMetaInfoBanner,
  getPageDescription,
  getPageTitle,
  getShowBadge,
  getShowPostConnectionUI,
} from './MetaIntegrationPage.utils';
import { MetaConnectedBadge, MetaConnectedView, MetaSettingsButton } from './PostConnection';
import { MetaConnectButton, MetaOnboardingView } from './PreConnection';

function MetaIntegrationPage(): JSX.Element {
  const account = useAccount();
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const { isLoading, status, error, integration, pages, refetch } = useMetaIntegration();

  const user = useUser();
  const { handleSync, isSyncing } = useMetaSync(integration);

  const showPostConnectionUI = getShowPostConnectionUI(integration);
  const showBadge = getShowBadge(integration);
  const infoBanner = getMetaInfoBanner(integration, t);
  const isInitialSetup = integration?.isConfiguring === true && !integration.setupCompletedAt;
  const showSyncAction = user.isSudoer && (isInitialSetup || Boolean(error));

  function getActions(): JSX.Element {
    if (showPostConnectionUI && integration) {
      return <MetaSettingsButton integration={integration} onDisconnect={refetch} />;
    }
    return <MetaConnectButton />;
  }

  function getChipsComponent(): JSX.Element | undefined {
    if (showBadge && status) {
      return (
        <Flex>
          <MetaConnectedBadge status={status} />
        </Flex>
      );
    }
    return undefined;
  }

  function getSyncAction(): JSX.Element | undefined {
    if (!showSyncAction) {
      return undefined;
    }
    return (
      <button disabled={isSyncing} onClick={handleSync}>
        {isSyncing ? t('Synchronizing…') : t('Synchronize')}
      </button>
    );
  }

  function getBanners(): JSX.Element | null {
    if (!error && !infoBanner) {
      return null;
    }
    return (
      <>
        {error && (
          <Alert description={error.message} title={error.title} type={'danger'}>
            {getSyncAction()}
          </Alert>
        )}
        {infoBanner && (
          <Alert description={infoBanner.message} title={infoBanner.title} type={infoBanner.type}>
            {isInitialSetup ? getSyncAction() : undefined}
          </Alert>
        )}
      </>
    );
  }

  function getContent(): JSX.Element {
    if (showPostConnectionUI) {
      return <MetaConnectedView integration={integration} pages={pages} />;
    }
    return <MetaOnboardingView />;
  }

  return (
    <Page
      actions={getActions()}
      ChipsComponent={getChipsComponent()}
      collapseHeader={false}
      collapseHeaderOnScroll={true}
      description={getPageDescription(showPostConnectionUI, t)}
      includeCurrentPageCrumb={true}
      loading={account.isPending || isLoading}
      showHeader={!isLoading}
      title={getPageTitle(showPostConnectionUI, t)}
    >
      <Stack gap={theme.spacing[2]}>
        {getBanners()}
        {getContent()}
      </Stack>
    </Page>
  );
}

export default observer(MetaIntegrationPage);
